import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Button, Card, Descriptions, Empty, Form, Input, InputNumber, Popconfirm, Select, Space, Spin, Table, Tabs, Tag,
    Typography, message, Grid,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { containerUserApi as api, containerBackupApi, isOk, errMsg } from '../../../shared/services/containerService';
import cdnService from '../../services/cdnService';
import {
    ContainerStatusTag, PowerButtons, UsageBlock, HealthDot, needsPoll, arr,
} from '../../../shared/components/container/ContainerParts';
import { ContainerTerminal } from '../../../shared/components/container/ContainerTools';
import ContainerFiles from '../../../shared/components/container/ContainerFiles';
import { NetworkTab, AppTab, ConfigTab } from './DetailTabs';
import AutoBackupCard from '../../../shared/components/evm/vmdetail/AutoBackupCard';

const { Text } = Typography;
const backupApi = containerBackupApi('user');
const resErr = (r, fb) => (r && (r.msg || r.error || r.message)) || fb;
const resOk = (r) => r && (r.success === true || r.status === 'success');

const DomainsTab = ({ c }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const r = await api.domains(c.id);
        if (isOk(r)) setData({ bound: arr(r.data?.bound), domains: arr(r.data?.domains) });
        else { setData({ bound: [], domains: [] }); message.error(errMsg(r, t('Failed to load'))); }
    }, [c.id, t]);
    useEffect(() => { load(); }, [load]);

    const bind = async (v) => {
        setSaving(true);
        const r = await cdnService.createHostname({
            domain_id: v.domain_id,
            hostname: v.hostname.trim(),
            origin: [{ type: 'container', container_id: c.id, port: v.port }],
        });
        setSaving(false);
        if (resOk(r)) { message.success(t('Bound')); form.resetFields(['hostname']); load(); } else message.error(resErr(r, t('Binding failed')));
    };
    const unbind = async (row) => {
        const r = await cdnService.deleteHostname(row.hostname_id);
        if (resOk(r)) { message.success(t('Unbound')); load(); } else message.error(resErr(r, t('Failed to unbind')));
    };

    if (!data) return <Spin />;
    const cols = [
        { title: t('Domains'), dataIndex: 'hostname' },
        { title: t('Container port'), dataIndex: 'port' },
        { title: t('Health'), dataIndex: 'healthy', render: (v) => <HealthDot healthy={v} /> },
        {
            title: '', key: 'op', width: 60, render: (_, r) => (
                <Popconfirm title={t('Unbind it?')} onConfirm={() => unbind(r)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {data.domains.length ? (
                <Form form={form} layout="inline" onFinish={bind} style={{ rowGap: 8 }}
                    initialValues={{ domain_id: data.domains[0]?.id, port: 80 }}>
                    <Form.Item name="domain_id" rules={[{ required: true }]}>
                        <Select style={{ width: 200 }} options={data.domains.map((d) => ({ value: d.id, label: d.domain }))} />
                    </Form.Item>
                    <Form.Item name="hostname" rules={[{ required: true, whitespace: true, message: t('Please enter a hostname') }]}
                        tooltip={t('Prefix such as www; @ means the root domain')}>
                        <Input placeholder={t('www or @')} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name="port" rules={[{ required: true, message: t('Enter the container port') }]}>
                        <InputNumber min={1} max={65535} placeholder={t('Container port')} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item><Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={saving}>{t('Bind domain')}</Button></Form.Item>
                </Form>
            ) : <Text type="secondary">{t('No CDN domains available')}</Text>}
            <Table size="small" rowKey="hostname_id" columns={cols} dataSource={data.bound} pagination={false}
                locale={{ emptyText: t('Nothing bound') }} />
        </Space>
    );
};

const noticeKey = (cid, n) => `ctr_notice_${cid}_${n.at}`;
const isDismissed = (k) => { try { return localStorage.getItem(k) === '1'; } catch (_) { return false; } };

/** 端口重新分配等通知，可关闭（记在本地）。 */
const Notices = ({ c }) => {
    const { t } = useTranslation();
    const [, force] = useState(0);
    const list = arr(c.notices).filter((n) => n && n.type === 'ports_reassigned' && !isDismissed(noticeKey(c.id, n)));
    if (!list.length) return null;
    const close = (n) => { try { localStorage.setItem(noticeKey(c.id, n), '1'); } catch (_) { /* ignore */ } force((x) => x + 1); };
    return list.map((n) => (
        <Alert key={n.at} type="warning" showIcon closable onClose={() => close(n)}
            message={`${t('Ports reassigned')}: ${arr(n.ports).map((p) => `${p.protocol} ${p.from}→${p.to}`).join(', ')}`} />
    ));
};

const ContainerDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [tab, setTab] = useState('network');
    const isMobile = !Grid.useBreakpoint().md;
    const timer = useRef(null);
    const termTicket = useCallback(() => api.terminal(id), [id]);
    const filesTicket = useCallback(() => api.files(id), [id]);

    const load = useCallback(async () => {
        const r = await api.get(id);
        if (isOk(r) && r.data) setC(r.data);
        else if (!c) { setNotFound(true); message.error(errMsg(r, t('Failed to load'))); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        clearTimeout(timer.current);
        if (needsPoll(c)) timer.current = setTimeout(load, 10000);
        return () => clearTimeout(timer.current);
    }, [c, load]);

    const back = () => navigate(userPath('/containers'));

    if (notFound) return <Card><Empty description={t('Container does not exist')}><Button onClick={back}>{t('Back')}</Button></Empty></Card>;
    if (!c) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

    return (
        <div>
            <PageHeader
                title={c.label || c.name || `#${c.id}`}
                onBack={back}
                tags={<ContainerStatusTag status={c.status} busy={c.busy} />}
                extra={<>
                    <Button icon={<SyncOutlined />} onClick={load} />
                    <PowerButtons c={c} api={api} type="default" size="middle" onDone={() => setTimeout(load, 1500)} onDeleted={back} />
                </>}
            />
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {c.status === 'error' && c.error_msg && <Text type="danger">{c.error_msg}</Text>}
                <Notices c={c} />
                <UsageBlock c={c} />
                <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
                    <Descriptions.Item label={t('Image')}><Text code>{c.image}</Text>{c.template && <Tag style={{ marginInlineStart: 8 }}>{c.template}</Tag>}</Descriptions.Item>
                    <Descriptions.Item label={t('Expires')}>{c.expires_at ? String(c.expires_at).slice(0, 10) : '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('Created at')}>{c.created_at || '-'}</Descriptions.Item>
                </Descriptions>
                <Card styles={{ body: { paddingTop: 4 } }}>
                    {/* 终端/文件/应用 持有 WebSocket，只渲染当前页，离开即关闭 */}
                    <Tabs activeKey={tab} onChange={setTab} items={[
                        { key: 'network', label: t('Network'), children: tab === 'network' && <NetworkTab c={c} reload={load} /> },
                        { key: 'app', label: t('App'), children: tab === 'app' && <AppTab c={c} reload={load} /> },
                        { key: 'terminal', label: t('Terminal'), children: tab === 'terminal' && <ContainerTerminal fetchTicket={termTicket} /> },
                        { key: 'files', label: t('File'), children: tab === 'files' && <ContainerFiles fetchTicket={filesTicket} root={c.files_root} /> },
                        { key: 'domains', label: t('Domains'), children: tab === 'domains' && <DomainsTab c={c} /> },
                        { key: 'config', label: t('Configuration'), children: tab === 'config' && <ConfigTab c={c} reload={load} /> },
                        { key: 'backup', label: t('Backups'), children: tab === 'backup' && <AutoBackupCard id={c.id} api={backupApi} adapter={backupApi} resType="container" isMobile={isMobile} active /> },
                    ]} />
                </Card>
            </Space>
        </div>
    );
};

export default ContainerDetail;
